<template>
  <div class="diagram-preview">
    <div class="container">
      <div class="header">
        <h1>Mermaid</h1>
        <p>支持 Mermaid 流程图、时序图、类图等图形代码的实时预览</p>
      </div>
      
      <div class="editor-container">
        <div class="toolbar">
          <div class="diagram-types">
            <button 
              v-for="type in diagramTypes" 
              :key="type.key"
              :class="['type-btn', { active: selectedType === type.key }]"
              @click="selectType(type.key)"
            >
              <i :class="type.icon"></i>
              {{ type.name }}
            </button>
          </div>
          <div class="actions">
            <button class="btn-action" @click="clearCode">
              <i class="bi bi-trash"></i>
              清空
            </button>
            <button class="btn-action" @click="copyCode">
              <i class="bi bi-clipboard"></i>
              复制
            </button>
          </div>
        </div>
        
        <div class="editor-layout">
          <div class="code-editor">
            <div class="editor-header">
              <span>代码编辑器</span>
              <span class="type-label">{{ getCurrentType().name }}</span>
            </div>
            <textarea 
              v-model="code" 
              class="code-input"
              :placeholder="getCurrentType().placeholder"
              @input="debounceRender"
            ></textarea>
          </div>
          
          <div class="preview-panel">
            <div class="preview-header">
              <span>预览结果</span>
              <div class="preview-actions">
                <button class="btn-export" @click="exportImage" :disabled="!code.trim() || error">
                  <i class="bi bi-download"></i>
                  导出图片
                </button>
                <button class="btn-fullscreen" @click="toggleFullscreen">
                  <i class="bi bi-arrows-fullscreen"></i>
                </button>
              </div>
            </div>
            <div class="preview-content" :class="{ fullscreen: isFullscreen }">
              <div v-if="isFullscreen" class="fullscreen-header">
                <span>预览结果</span>
                <div class="fullscreen-actions">
                  <button class="btn-export" @click="exportImage" :disabled="!code.trim() || error">
                    <i class="bi bi-download"></i>
                    导出图片
                  </button>
                  <button class="btn-exit-fullscreen" @click="toggleFullscreen">
                    <i class="bi bi-fullscreen-exit"></i>
                    退出全屏
                  </button>
                </div>
              </div>
              <div v-if="error" class="error-message">
                <i class="bi bi-exclamation-triangle"></i>
                <p>{{ error }}</p>
              </div>
              <div v-else-if="!code.trim()" class="empty-state">
                <i class="bi bi-diagram-3"></i>
                <p>在左侧输入图形代码，这里将显示预览结果</p>
              </div>
              <div v-else id="diagram-container" class="diagram-output"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mermaid from 'mermaid'

export default {
  name: 'DiagramPreview',
  data() {
    return {
      selectedType: 'flowchart',
      code: '',
      error: null,
      isFullscreen: false,
      renderTimeout: null,
      diagramTypes: [

        {
          key: 'flowchart',
          name: '流程图',
          icon: 'bi bi-star-fill',
          placeholder: `flowchart TD
    Start --> Decision{Decision?}
    Decision -->|Yes| Process1[Process 1]
    Decision -->|No| Process2[Process 2]
    Process1 --> End
    Process2 --> End`,
          example: `flowchart TD
    Start --> Decision{Decision?}
    Decision -->|Yes| Process1[Process 1]
    Decision -->|No| Process2[Process 2]
    Process1 --> End
    Process2 --> End`
        },
        {
          key: 'sequence',
          name: '时序图',
          icon: 'bi bi-arrow-left-right',
          placeholder: `sequenceDiagram
    participant A as 用户
    participant B as 系统
    A->>B: 发送请求
    B-->>A: 返回响应`,
          example: `sequenceDiagram
    participant A as 用户
    participant B as 系统
    A->>B: 发送请求
    B-->>A: 返回响应`
        },
        {
          key: 'gantt',
          name: '甘特图',
          icon: 'bi bi-calendar-range',
          placeholder: `gantt
    title 项目计划
    dateFormat  YYYY-MM-DD
    section 阶段1
    任务1    :a1, 2024-01-01, 30d
    任务2    :after a1, 20d`,
          example: `gantt
    title 项目计划
    dateFormat  YYYY-MM-DD
    section 阶段1
    任务1    :a1, 2024-01-01, 30d
    任务2    :after a1, 20d`
        },
        {
          key: 'pie',
          name: '饼图',
          icon: 'bi bi-pie-chart',
          placeholder: `pie title 市场份额分布
    "Chrome" : 65.2
    "Safari" : 18.4
    "Firefox" : 9.1
    "Edge" : 4.8
    "其他" : 2.5`,
          example: `pie title 市场份额分布
    "Chrome" : 65.2
    "Safari" : 18.4
    "Firefox" : 9.1
    "Edge" : 4.8
    "其他" : 2.5`
        },
        {
          key: 'gitgraph',
          name: 'Git图',
          icon: 'bi bi-git',
          placeholder: `gitGraph
    commit
    commit
    branch develop
    checkout develop
    commit
    commit
    checkout main
    merge develop
    commit`,
          example: `gitGraph
    commit
    commit
    branch develop
    checkout develop
    commit
    commit
    checkout main
    merge develop
    commit`
        },
        {
          key: 'erdiagram',
          name: 'ER图',
          icon: 'bi bi-diagram-2',
          placeholder: `erDiagram
    USER {
        int id PK
        string name
        string email
    }
    ORDER {
        int id PK
        int user_id FK
        date order_date
    }
    USER ||--o{ ORDER : places`,
          example: `erDiagram
    USER {
        int id PK
        string name
        string email
    }
    ORDER {
        int id PK
        int user_id FK
        date order_date
    }
    USER ||--o{ ORDER : places`
        },
        {
          key: 'statediagram',
          name: '状态图',
          icon: 'bi bi-circle-square',
          placeholder: `stateDiagram-v2
    [*] --> 空闲
    空闲 --> 运行 : 启动
    运行 --> 空闲 : 停止
    运行 --> 故障 : 错误
    故障 --> 空闲 : 修复
    空闲 --> [*]`,
          example: `stateDiagram-v2
    [*] --> 空闲
    空闲 --> 运行 : 启动
    运行 --> 空闲 : 停止
    运行 --> 故障 : 错误
    故障 --> 空闲 : 修复
    空闲 --> [*]`
        },
        {
          key: 'class',
          name: '类图',
          icon: 'bi bi-boxes',
          placeholder: `classDiagram
    class Animal {
        +String name
        +int age
        +makeSound()
    }
    class Dog {
        +bark()
    }
    Animal <|-- Dog`,
          example: `classDiagram
    class Animal {
        +String name
        +int age
        +makeSound()
    }
    class Dog {
        +bark()
    }
    Animal <|-- Dog`
        },
        {
          key: 'mindmap',
          name: '思维导图',
          icon: 'bi bi-diagram-3-fill',
          placeholder: `mindmap
  root((项目规划))
    需求分析
      用户调研
      竞品分析
    设计阶段
      UI设计
      交互设计
    开发阶段
      前端开发
      后端开发
    测试阶段
      单元测试
      集成测试`,
          example: `mindmap
  root((项目规划))
    需求分析
      用户调研
      竞品分析
    设计阶段
      UI设计
      交互设计
    开发阶段
      前端开发
      后端开发
    测试阶段
      单元测试
      集成测试`
        },
        {
          key: 'timeline',
          name: '时间线',
          icon: 'bi bi-clock-history',
          placeholder: `timeline
    title 产品发展历程
    2021 : 项目启动
         : 需求调研
    2022 : 产品设计
         : 开发阶段
    2023 : 测试上线
         : 用户反馈
    2024 : 功能迭代
         : 市场推广`,
          example: `timeline
    title 产品发展历程
    2021 : 项目启动
         : 需求调研
    2022 : 产品设计
         : 开发阶段
    2023 : 测试上线
         : 用户反馈
    2024 : 功能迭代
         : 市场推广`
        }
      ]
    }
  },
  mounted() {
    mermaid.initialize({ 
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose'
    })
    this.code = this.getCurrentType().example
    this.$nextTick(() => {
      this.renderDiagram()
    })
  },
  methods: {
    getCurrentType() {
      return this.diagramTypes.find(type => type.key === this.selectedType)
    },
    selectType(type) {
      this.selectedType = type
      this.code = this.getCurrentType().example
      this.error = null
      this.$nextTick(() => {
        this.renderDiagram()
      })
    },
    debounceRender() {
      clearTimeout(this.renderTimeout)
      this.renderTimeout = setTimeout(() => {
        this.renderDiagram()
      }, 500)
    },
    async renderDiagram() {
      if (!this.code.trim()) {
        this.error = null
        return
      }
      
      try {
        this.error = null
        const container = document.getElementById('diagram-container')
        if (!container) return
        
        container.innerHTML = ''
        
        const id = 'diagram-' + Date.now()
        const isValid = await mermaid.parse(this.code)
        
        if (isValid) {
          const { svg } = await mermaid.render(id, this.code)
          container.innerHTML = svg
        }
      } catch (err) {
        this.error = '图形代码解析错误: ' + err.message
      }
    },
    clearCode() {
      this.code = ''
      this.error = null
      document.getElementById('diagram-container').innerHTML = ''
    },
    copyCode() {
      navigator.clipboard.writeText(this.code).then(() => {
        this.$message?.success?.('代码已复制到剪贴板') || alert('代码已复制')
      })
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    },
    exportImage() {
      const container = document.getElementById('diagram-container')
      const svg = container.querySelector('svg')
      if (!svg) return
      
      try {
        const scale = 4 // Higher scale for better quality
        
        // Create high-resolution temporary canvas
        const tempCanvas = document.createElement('canvas')
        const tempCtx = tempCanvas.getContext('2d')
        const img = new Image()
        
        // Get original SVG dimensions
        const svgRect = svg.getBoundingClientRect()
        const originalWidth = svgRect.width || svg.clientWidth
        const originalHeight = svgRect.height || svg.clientHeight
        
        const svgData = new XMLSerializer().serializeToString(svg)
        
        img.onload = () => {
          // Set high resolution for temporary canvas
          tempCanvas.width = originalWidth * scale
          tempCanvas.height = originalHeight * scale
          
          // Enable image smoothing for better quality
          tempCtx.imageSmoothingEnabled = true
          tempCtx.imageSmoothingQuality = 'high'
          
          // Draw at high resolution
          tempCtx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height)
          
          // Get image data to find actual content bounds
          const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
          const data = imageData.data
          
          let minX = tempCanvas.width, minY = tempCanvas.height
          let maxX = 0, maxY = 0
          
          // Scan pixels to find non-white content (sample every few pixels for performance)
          const step = 2
          for (let y = 0; y < tempCanvas.height; y += step) {
            for (let x = 0; x < tempCanvas.width; x += step) {
              const idx = (y * tempCanvas.width + x) * 4
              const r = data[idx]
              const g = data[idx + 1]
              const b = data[idx + 2]
              const a = data[idx + 3]
              
              // Check if pixel is not white/transparent
              if (a > 0 && (r < 250 || g < 250 || b < 250)) {
                minX = Math.min(minX, x)
                minY = Math.min(minY, y)
                maxX = Math.max(maxX, x)
                maxY = Math.max(maxY, y)
              }
            }
          }
          
          // Add padding
          const padding = 20 * scale
          minX = Math.max(0, minX - padding)
          minY = Math.max(0, minY - padding)
          maxX = Math.min(tempCanvas.width, maxX + padding)
          maxY = Math.min(tempCanvas.height, maxY + padding)
          
          const cropWidth = maxX - minX
          const cropHeight = maxY - minY
          
          // Create final high-quality canvas
          const finalCanvas = document.createElement('canvas')
          const finalCtx = finalCanvas.getContext('2d')
          
          finalCanvas.width = cropWidth
          finalCanvas.height = cropHeight
          
          // Enable high-quality rendering
          finalCtx.imageSmoothingEnabled = true
          finalCtx.imageSmoothingQuality = 'high'
          
          // Fill with white background
          finalCtx.fillStyle = 'white'
          finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)
          
          // Draw cropped content at full resolution
          finalCtx.drawImage(
            tempCanvas,
            minX, minY, cropWidth, cropHeight,
            0, 0, cropWidth, cropHeight
          )
          
          try {
            finalCanvas.toBlob(blob => {
              if (blob) {
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `mermaid-${this.selectedType}-${Date.now()}.png`
                a.click()
                URL.revokeObjectURL(url)
              } else {
                const dataURL = finalCanvas.toDataURL('image/png', 1.0)
                const a = document.createElement('a')
                a.href = dataURL
                a.download = `mermaid-${this.selectedType}-${Date.now()}.png`
                a.click()
              }
            }, 'image/png', 1.0)
          } catch (err) {
            const dataURL = finalCanvas.toDataURL('image/png', 1.0)
            const a = document.createElement('a')
            a.href = dataURL
            a.download = `mermaid-${this.selectedType}-${Date.now()}.png`
            a.click()
          }
        }
        
        img.onerror = () => {
          console.error('Failed to load SVG image')
        }
        
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
      } catch (err) {
        console.error('Export failed:', err)
        alert('导出失败，请重试')
      }
    }
  }
}
</script>

<style scoped>
.diagram-preview {
  padding: 40px 0;
  background: #f8f9fa;
  min-height: calc(100vh - 140px);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.header {
  text-align: center;
  margin-bottom: 40px;
  max-width: calc(100vw - 40px);
  width: 100%;
}

.header h1 {
  font-size: 36px;
  font-weight: 300;
  margin-bottom: 15px;
  color: #333;
}

.header p {
  color: #666;
  font-size: 16px;
}

.editor-container {
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  overflow: hidden;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
}

.diagram-types {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.type-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
}

.type-btn:hover {
  border-color: #4a90e2;
  color: #4a90e2;
}

.type-btn.active {
  background: #4a90e2;
  color: white;
  border-color: #4a90e2;
}

.actions {
  display: flex;
  gap: 10px;
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
}

.btn-action:hover {
  background: #f5f5f5;
}

.syntax-link {
  text-decoration: none;
  color: inherit;
}

.syntax-link:hover {
  color: #4a90e2;
  background: #f0f8ff;
}

.editor-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 600px;
}

.code-editor, .preview-panel {
  display: flex;
  flex-direction: column;
}

.code-editor {
  border-right: 1px solid #eee;
}

.editor-header, .preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  font-weight: 500;
  color: #333;
}

.preview-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-export {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border: 1px solid #4a90e2;
  background: #4a90e2;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
}

.btn-export:hover:not(:disabled) {
  background: #357abd;
  border-color: #357abd;
}

.btn-export:disabled {
  background: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.type-label {
  font-size: 12px;
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
}

.btn-fullscreen {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.3s;
}

.btn-fullscreen:hover {
  background: #e0e0e0;
}

.code-input {
  flex: 1;
  border: none;
  padding: 20px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;
  background: #fafafa;
}

.preview-content {
  flex: 1;
  padding: 20px;
  overflow: auto;
  background: white;
}

.preview-content.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: white;
  display: flex;
  flex-direction: column;
}

.fullscreen-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  font-weight: 500;
  color: #333;
}

.fullscreen-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-exit-fullscreen {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
}

.btn-exit-fullscreen:hover {
  background: #f5f5f5;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #d32f2f;
  background: #ffebee;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #d32f2f;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  text-align: center;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
}

.diagram-output {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.diagram-output svg {
  max-width: 100%;
  height: auto;
}

@media (max-width: 768px) {
  .editor-layout {
    grid-template-columns: 1fr;
    height: auto;
  }
  
  .code-editor {
    border-right: none;
    border-bottom: 1px solid #eee;
  }
  
  .toolbar {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .diagram-types {
    flex-wrap: wrap;
  }
}
</style>